import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MedicalEntityWithSource } from '@/types/clinicalValidation';
import { Badge } from '@/components/ui/badge';
import {
  Clock,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Play
} from 'lucide-react';
import {
  SPEAKER_ROLE_CONFIG,
  SPEAKER_ROLE_ORDER,
  normalizeSpeakerRole,
  type DisplaySpeakerRole,
} from '@/utils/speakerRoleConfig';

// UNIFIED SEGMENT STRUCTURE: Single source of truth with all enrichments
interface TranscriptSegment {
  id: string;
  start: number;
  end: number;
  text: string;
  confidence: number;
  
  // Speaker enrichment (from UnifiedSpeakerAnalysisService)
  speaker?: {
    id: string;
    role: DisplaySpeakerRole;
    displayName: string;
    confidence: number;
  };
  
  // Content classification (from ContentClassificationAgent)
  classification?: {
    category: 'clinical' | 'administrative' | 'social' | 'procedural';
    subcategory: string;
    clinicalRelevance: number;
    medicalPriority: 'high' | 'medium' | 'low';
    containsPHI: boolean;
    containsMedicalTerms: boolean;
  };
  
  // Medical entities (from MedicalIntelligenceAgent)  
  medicalEntities?: Array<{
    id: string;
    text: string;
    type: 'symptom' | 'medication' | 'diagnosis' | 'procedure' | 'anatomical_site';
    confidence: number;
    startIndex: number;
    endIndex: number;
    imoCode?: string;
  }>;
  
  // Processing metadata
  enrichment?: {
    processedBy: string[];
    lastEnriched: Date;
    version: string;
  };
  
  // BACKWARD COMPATIBILITY: Keep old fields as optional
  speakerRole?: string; // DEPRECATED: Use speaker.role instead
}

interface TranscriptViewerProps {
  segments: TranscriptSegment[];
  highlightedSegmentId?: string;
  selectedEntityId?: string;
  medicalEntities: MedicalEntityWithSource[];
  onSegmentSelect: (segmentId: string) => void;
  onSegmentSeek?: (startTimeSeconds: number) => void;
  compact?: boolean;
}

export const TranscriptViewer: React.FC<TranscriptViewerProps> = ({
  segments,
  highlightedSegmentId,
  selectedEntityId,
  medicalEntities,
  onSegmentSelect,
  onSegmentSeek,
  compact = false
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<{ [key: string]: HTMLDivElement }>({});

  // Distinct speaker roles actually present, in canonical order (drives the legend)
  const presentRoles = useMemo(() => {
    const seen = new Set<DisplaySpeakerRole>();
    segments.forEach((s) => seen.add(normalizeSpeakerRole(s.speaker?.role || s.speakerRole)));
    return SPEAKER_ROLE_ORDER.filter((r) => seen.has(r));
  }, [segments]);

  // Scroll to highlighted segment
  useEffect(() => {
    if (highlightedSegmentId && segmentRefs.current[highlightedSegmentId]) {
      segmentRefs.current[highlightedSegmentId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [highlightedSegmentId]);

  // Get entities for a specific segment (UNIFIED: Use direct segment entities)
  const getSegmentEntities = useCallback((segmentId: string) => {
    const segment = segments.find(s => s.id === segmentId);
    if (segment?.medicalEntities) {
      // NEW: Use unified segment medical entities directly
      return segment.medicalEntities.map(entity => ({
        id: entity.id || `${segmentId}-${entity.text}`,
        entity: {
          text: entity.text,
          type: entity.type,
          confidence: entity.confidence,
          imoCode: entity.imoCode
        },
        source: {
          segmentId: segmentId,
          exactQuote: entity.text,
          startIndex: entity.startIndex,
          endIndex: entity.endIndex
        }
      }));
    }
    
    // FALLBACK: Use legacy external medicalEntities array for backward compatibility
    return medicalEntities.filter(entity => entity.source.segmentId === segmentId);
  }, [segments, medicalEntities]);

  // Highlight medical entities in text
  const highlightEntitiesInText = useCallback((text: string, segmentId: string) => {
    const segmentEntities = getSegmentEntities(segmentId);
    if (segmentEntities.length === 0) return text;

    // Create a simple text-based matching approach since we have exactQuote
    let highlightedText = text;
    
    segmentEntities.forEach(entity => {
      const entityText = entity.entity.text;
      const exactQuote = entity.source.exactQuote;
      const isSelected = entity.id === selectedEntityId;
      
      // Use exactQuote if available, otherwise fall back to entity text
      const searchText = exactQuote || entityText;
      
      // Create a safe regex pattern
      const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`\\b${escapeRegex(searchText)}\\b`, 'gi');
      
      const highlightClass = `inline-block px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 cursor-pointer ${
        isSelected 
          ? 'bg-blue-200 text-blue-900 border-2 border-blue-400 ring-2 ring-blue-300' 
          : getEntityHighlightClass(entity.entity.type, entity.entity.confidence)
      }`;
      
      // Only replace if not already highlighted (avoid double highlighting)
      if (!highlightedText.includes(`data-entity-id="${entity.id}"`)) {
        highlightedText = highlightedText.replace(pattern, (match) => {
          return `<span class="${highlightClass}" data-entity-id="${entity.id}" title="${entity.entity.type}: ${entityText} (${Math.round(entity.entity.confidence * 100)}% confidence)">${match}</span>`;
        });
      }
    });

    return highlightedText;
  }, [selectedEntityId, getSegmentEntities]);

  // Get highlight class based on entity type and confidence
  const getEntityHighlightClass = (type: string, confidence: number) => {
    const baseClass = 'hover:scale-105 hover:shadow-md';
    const confidenceClass = confidence > 0.9 ? 'border border-opacity-50' : 
                           confidence > 0.7 ? 'border border-opacity-30' : 
                           'border border-dashed border-opacity-40';
    
    switch (type) {
      case 'symptom':
        return `bg-red-100 text-red-800 border-red-300 ${confidenceClass} ${baseClass}`;
      case 'medication':
        return `bg-blue-100 text-blue-800 border-blue-300 ${confidenceClass} ${baseClass}`;
      case 'diagnosis':
        return `bg-purple-100 text-purple-800 border-purple-300 ${confidenceClass} ${baseClass}`;
      case 'procedure':
        return `bg-green-100 text-green-800 border-green-300 ${confidenceClass} ${baseClass}`;
      case 'anatomical_site':
        return `bg-orange-100 text-orange-800 border-orange-300 ${confidenceClass} ${baseClass}`;
      default:
        return `bg-gray-100 text-gray-800 border-gray-300 ${confidenceClass} ${baseClass}`;
    }
  };

  // Format timestamp
  const formatTimestamp = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get speaker icon (UNIFIED: Use speaker.role from unified structure)
  const getSpeakerIcon = (segment: TranscriptSegment) =>
    SPEAKER_ROLE_CONFIG[normalizeSpeakerRole(segment.speaker?.role || segment.speakerRole)].icon(16);

  // Handle entity click in transcript
  const handleEntityClick = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const entityId = target.getAttribute('data-entity-id');
    if (entityId) {
      event.preventDefault();
      event.stopPropagation();
      // Find the entity and trigger selection
      const entity = medicalEntities.find(e => e.id === entityId);
      if (entity) {
        console.log('Entity clicked in transcript:', entityId, entity.entity.text);
        // This should be handled by parent component via props if needed
      }
    }
  }, [medicalEntities]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      {!compact && (
        <div className="p-4 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <MessageSquare className="text-blue-600" size={20} />
              Conversation Transcript
            </h3>
            <Badge variant="outline" className="text-xs">
              {segments.length} segments
            </Badge>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Click on highlighted medical terms to view details
          </p>
        </div>
      )}

      {/* Speaker Legend (dynamic: only roles actually present in this encounter) */}
      {presentRoles.length > 0 && (
        <div className="p-2 bg-gray-50/80 border-b border-gray-200">
          <div className={`flex flex-wrap ${compact ? 'gap-2 text-[10px]' : 'gap-3 text-xs'}`}>
            <div className={`font-medium text-gray-700`}>Speakers:</div>
            {presentRoles.map((role) => {
              const cfg = SPEAKER_ROLE_CONFIG[role];
              return (
                <div key={role} className="flex items-center gap-1">
                  {cfg.icon(compact ? 10 : 12)}
                  <span className={cfg.legendTextColor}>{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Transcript Segments */}
      <div 
        ref={scrollRef}
        className={`flex-1 overflow-y-auto ${compact ? 'p-3' : 'p-4'} space-y-4`}
        onClick={handleEntityClick}
      >
        <AnimatePresence>
          {segments.map((segment, index) => {
            const isHighlighted = segment.id === highlightedSegmentId;
            const segmentEntities = getSegmentEntities(segment.id);
            const hasEntities = segmentEntities.length > 0;
            const hasSelectedEntity = selectedEntityId && segmentEntities.some(e => e.id === selectedEntityId);

            // Get background color based on speaker role (UNIFIED: Use speaker.role)
            const getSpeakerBackground = () => {
              if (isHighlighted && hasSelectedEntity) {
                return 'bg-blue-100 border-blue-400 shadow-lg ring-2 ring-blue-200';
              }
              if (isHighlighted) {
                return 'bg-blue-50 border-blue-300 shadow-md';
              }
              
              // Speaker role-based colors when not highlighted (UNIFIED structure)
              const role = normalizeSpeakerRole(segment.speaker?.role || segment.speakerRole);
              return SPEAKER_ROLE_CONFIG[role].bg(hasEntities);
            };

            return (
              <motion.div
                key={segment.id}
                ref={el => el && (segmentRefs.current[segment.id] = el)}
                className={`group relative p-4 rounded-lg border transition-all duration-200 cursor-pointer ${getSpeakerBackground()}`}
                onClick={() => onSegmentSelect(segment.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.02 }}
                whileHover={{ scale: 1.01 }}
              >
                {/* Speaker Header (UNIFIED: Use speaker.displayName) */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getSpeakerIcon(segment)}
                    <span className="font-medium text-gray-800">
                      {segment.speaker?.displayName ||
                       SPEAKER_ROLE_CONFIG[normalizeSpeakerRole(segment.speaker?.role || segment.speakerRole)].label}
                    </span>
                    {hasEntities && (
                      <Badge variant="outline" className="text-xs">
                        {segmentEntities.length} {segmentEntities.length === 1 ? 'entity' : 'entities'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {onSegmentSeek ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSegmentSeek(segment.start);
                        }}
                        className="flex items-center gap-1 hover:text-blue-500 transition-colors"
                        title="Play from here"
                      >
                        <Play size={10} className="fill-current" />
                        <span>{formatTimestamp(segment.start)}</span>
                      </button>
                    ) : (
                      <>
                        <Clock size={12} />
                        <span>{formatTimestamp(segment.start)}</span>
                      </>
                    )}
                    <span className="text-gray-400">•</span>
                    <span>{Math.round(segment.confidence * 100)}%</span>
                  </div>
                </div>

                {/* Transcript Text with Entity Highlighting */}
                <div 
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ 
                    __html: highlightEntitiesInText(segment.text, segment.id) 
                  }}
                />

                {/* Entity Count Indicator */}
                {hasEntities && (
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Scroll Indicators */}
      <div className="p-2 bg-white/80 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-500">
          {highlightedSegmentId && selectedEntityId ? (
            <span className="text-blue-600 font-medium">
              📍 Showing selected medical entity in conversation
            </span>
          ) : highlightedSegmentId ? (
            'Viewing highlighted segment'
          ) : (
            'Click segments to view details or select entities from Medical Intelligence'
          )}
        </p>
      </div>
    </div>
  );
};